import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { components } from "@/api/contract";
import { queryKeys } from "@/api/query-keys";
import { getImageJob } from "../api/getImageJob";

const POLL_INTERVAL_MS = 2_000;
const TIMEOUT_MS = 90_000;

/** Terminal phase of a polled image job — drives the chat tool card. */
export type ImageJobPhase = "generating" | "succeeded" | "failed";

/** A single produced image from the job (url + dimensions + ThumbHash blur). */
export type ImageJobOutput = components["schemas"]["ImageOutput"];

/** Phase + first produced output — the tool card renders the placeholder→image. */
export interface ImageJobStatus {
  phase: ImageJobPhase;
  output: ImageJobOutput | null;
}

/**
 * Polls an async `generate_image` job (`GET /v1/image/jobs/{id}`) every ~2s
 * until it reaches a terminal state, capped at ~90s. Returns the coarse phase
 * plus the first produced output so the tool card can render a placeholder →
 * spinner → image. Pass `null` to stay idle.
 */
export function useImageJobStatus(jobId: string | null): ImageJobStatus {
  // Once the job (or the poll cap) resolves we stop hitting the network; the
  // start time gates the timeout independently of query lifecycle.
  const startedAt = useRef<number>(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    startedAt.current = Date.now();
    setTimedOut(false);
  }, [jobId]);

  const { data } = useQuery({
    queryKey: queryKeys.chat.imageJob(jobId ?? ""),
    queryFn: () => getImageJob(jobId as string),
    enabled: jobId !== null && !timedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "succeeded" || status === "failed" || status === "cancelled") return false;
      if (Date.now() - startedAt.current > TIMEOUT_MS) return false;
      return POLL_INTERVAL_MS;
    },
    staleTime: 0,
    gcTime: 0,
  });

  const status = data?.status;

  // Trip a one-way timeout flag so polling can't resume after the cap.
  useEffect(() => {
    if (jobId === null || status === "succeeded" || status === "failed" || status === "cancelled") {
      return;
    }
    const remaining = TIMEOUT_MS - (Date.now() - startedAt.current);
    const id = setTimeout(() => setTimedOut(true), Math.max(0, remaining));
    return () => clearTimeout(id);
  }, [jobId, status]);

  const output = data?.outputs?.[0] ?? null;

  if (status === "succeeded") return { phase: "succeeded", output };
  if (status === "failed" || status === "cancelled") return { phase: "failed", output };
  if (timedOut) return { phase: "failed", output };
  return { phase: "generating", output };
}
